import React from 'react'
import { ChartData, HealthGradient } from '@percept/mui/charts'
import { AppTheme } from '@percept/mui'
import { TimeseriesHealthDatum } from '@percept/hooks'
import { TimelineDatum } from '@percept/app-components'
import { getDateLabelsForGranularity, isoDate } from '@percept/utils'
import { TimeseriesGranularity } from '@percept/types'
import { sortBy } from 'lodash-es'


export const HEALTH_GRADIENT_ID = 'healthGradient'

export const HealthDefs = ({
  appTheme,
}: {
  appTheme: AppTheme
}): JSX.Element => (
  <HealthGradient
    id={HEALTH_GRADIENT_ID}
    colourScale={appTheme.chart.healthColourScale}
    gradientTransform='rotate(0)' />
)


export const healthTimeseriesToChartData = ({
  timeseries,
  appTheme,
}: {
  timeseries: TimeseriesHealthDatum[]
  appTheme: AppTheme
}): ChartData<TimelineDatum> => {
  return timeseries.map( t => ({
    value: t.health,
    label: t.start.getTime(),
    start: t.start.getTime(),
    end: t.end.getTime(),
    fill: `url(#${HEALTH_GRADIENT_ID})`,
    color: appTheme.chart.healthColourScale(t.health || 0),
  }))
}

export const getTimeseriesDateRange = (timeseries: TimeseriesHealthDatum[]): [Date, Date] | null => {
  if( !timeseries.length ){
    return null
  }
  return [timeseries[0].start, timeseries[timeseries.length - 1].start]
}


export const getFullTimeseries = ({
  timeseries,
  granularity,
  dateRange,
  datumDefaults = {},
}: {
  timeseries: ChartData<TimelineDatum>
  dateRange: [Date, Date] | null
  granularity: TimeseriesGranularity
  datumDefaults?: Partial<Omit<TimelineDatum, 'start' | 'end' | 'label'>>
}): ChartData<TimelineDatum> => {
  if( dateRange === null ){
    return []
  }

  const labels = getDateLabelsForGranularity(dateRange, granularity)

  const isoTimeseriesMap: Record<string, TimelineDatum> = timeseries.reduce( (acc, d) => {
    acc[isoDate(new Date(d.start))] = d
    return acc
  }, {} as Record<string, TimelineDatum>)

  for( const d of labels ){
    if( !isoTimeseriesMap[d] ){
      const date = new Date(d).getTime()
      isoTimeseriesMap[d] = {
        start: date,
        end: date,
        label: date,
        value: null,
        ...datumDefaults,
      }
    }
  }

  return sortBy(Object.values(isoTimeseriesMap), 'start')
}
